// ═══════════════════════════════════════════════════════════════
// 📘 MÓDULO 9 - UNIDAD 2: OBLIGATIONS (Must & Have to)
// ═══════════════════════════════════════════════════════════════
// ✅ CORRECCIÓN: context a nivel SET, no por pregunta
// ✅ CORRECCIÓN: "questions" en lugar de "exercises"
// ═══════════════════════════════════════════════════════════════

export const M9_UNIT_2 = {
  id: "m9-u2-obligations",
  title: "Unidad 2: Obligations",
  topic: "Express obligations and rules using must, have to, must not, and don't have to in medical and everyday contexts.",
  iconType: "stethoscope",

  sets: [
    // ───────────────────────────────────────────────────────────
    // 🏥 SET 1: MUST - Strong Obligation (Doctor's Orders)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u2-s1",
      title: "Set 1: Must - Doctor's Orders",
      icon: "💊",
      context: `**Hospital St. Mary's - Emergency Room**

El Dr. Chen está atendiendo pacientes en urgencias. Como médico, debe dar instrucciones claras y firmes sobre el tratamiento. Cuando algo es ABSOLUTAMENTE NECESARIO para la salud del paciente, usa "MUST".

**St. Mary's Hospital - Emergency Room**

Dr. Chen is treating patients in the ER. As a doctor, he must give clear, firm instructions about treatment. When something is ABSOLUTELY NECESSARY for the patient's health, he uses "MUST".`,

      questions: [
        {
          type: "mc",
          q: "**Dr. Chen to patient with broken leg:** 'You ___ rest for at least three weeks, or your leg won't heal properly.'",
          options: ["must", "can", "might", "should"],
          a: "must",
          successTip: "💡 **MUST** expresses strong obligation or necessity, especially for doctor's orders!"
        },
        {
          type: "mc",
          q: "**Nurse to post-surgery patient:** 'You ___ take this medication twice a day. It's essential for preventing infection.'",
          options: ["must", "would", "could", "may"],
          a: "must",
          successTip: "✅ When something is medically essential, use **must**!"
        },
        {
          type: "mc",
          q: "**Doctor Martinez:** 'Your son ___ wear a helmet when cycling. Head injuries can be very serious.'",
          options: ["must", "might", "can", "will"],
          a: "must",
          successTip: "🎯 **MUST** shows something is absolutely necessary for safety!"
        },
        {
          type: "input",
          q: "**Pharmacist to diabetic patient:** 'You ___ eat before taking these pills, or you'll feel very sick.'",
          alts: ["must"],
          a: "must",
          successTip: "💊 Perfect! **MUST** is essential for critical medical instructions!"
        },
        {
          type: "mc",
          q: "**Physical therapist:** 'You ___ do these exercises every day if you want your shoulder to recover fully.'",
          options: ["must", "could", "might", "would"],
          a: "must",
          successTip: "🏋️ Excellent! **MUST** emphasizes the necessity of the action!"
        },
        {
          type: "mc",
          q: "**Emergency room doctor:** 'We ___ operate immediately. There's no time to waste.'",
          options: ["must", "can", "may", "should"],
          a: "must",
          successTip: "🚨 Great choice! **MUST** shows urgent necessity in emergencies!"
        },
        {
          type: "mc",
          q: "**Dentist to patient with cavity:** 'You ___ brush your teeth twice a day. Your dental hygiene needs improvement.'",
          options: ["must", "might", "could", "would"],
          a: "must",
          successTip: "🦷 Spot on! **MUST** emphasizes strong professional advice!"
        },
        {
          type: "input",
          q: "**Surgeon:** 'You ___ not lift anything heavy for six weeks after the operation.'",
          alts: ["must"],
          a: "must",
          successTip: "⚠️ Perfect! **MUST NOT** expresses prohibition for medical safety!"
        },
        {
          type: "mc",
          q: "**Doctor:** 'You ___ stop smoking if you want to avoid serious health problems.'",
          options: ["must", "can", "may", "could"],
          a: "must",
          successTip: "🚭 Excellent! **MUST** shows strong obligation for health!"
        },
        {
          type: "mc",
          q: "**Cardiologist:** 'You ___ reduce your salt intake. Your blood pressure is dangerously high.'",
          options: ["must", "might", "could", "would"],
          a: "must",
          successTip: "❤️ Outstanding! **MUST** emphasizes medical necessity!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🏥 SET 2: HAVE TO - External Obligation
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u2-s2",
      title: "Set 2: Have To - External Rules",
      icon: "📋",
      context: `**City Hospital - Reception & Administration**

Estás en la recepción del hospital. Aquí hay REGLAS y POLÍTICAS que todos deben seguir. Cuando algo es obligatorio por REGLAS EXTERNAS (del hospital, la ley, o el sistema), usamos "HAVE TO".

**City Hospital - Reception & Administration**

You're at the hospital reception. Here there are RULES and POLICIES that everyone must follow. When something is mandatory because of EXTERNAL RULES (from the hospital, the law, or the system), we use "HAVE TO".`,

      questions: [
        {
          type: "mc",
          q: "**Receptionist at clinic:** 'All patients ___ arrive 15 minutes before their appointment time. It's hospital policy.'",
          options: ["have to", "must", "can", "should"],
          a: "have to",
          successTip: "📋 **HAVE TO** expresses external rules or regulations!"
        },
        {
          type: "mc",
          q: "**Security at hospital entrance:** 'Sorry, visitors ___ wear a mask inside the building. It's the new regulation.'",
          options: ["have to", "must", "could", "might"],
          a: "have to",
          successTip: "😷 Perfect! **HAVE TO** shows external requirements!"
        },
        {
          type: "input",
          q: "**Nurse:** 'You ___ fill out this form before we can treat you. It's required by law.'",
          alts: ["have to", "have got to"],
          a: "have to",
          successTip: "📝 Great! **HAVE TO** indicates external legal obligations!"
        },
        {
          type: "mc",
          q: "**Pharmacist:** 'You ___ show your prescription to buy these pills. We can't sell them without it.'",
          options: ["have to", "must", "should", "can"],
          a: "have to",
          successTip: "💊 Excellent! **HAVE TO** for legal requirements in healthcare!"
        },
        {
          type: "mc",
          q: "**Doctor's receptionist:** 'I'm sorry, but she ___ see patients with appointments first. That's our system.'",
          options: ["has to", "must", "should", "can"],
          a: "has to",
          successTip: "🕐 Perfect! **HAS TO** (third person) for external rules!"
        },
        {
          type: "order",
          q: "**Lab technician to patient:** Arrange the sentence about mandatory pre-test procedures:",
          words: ["All", "patients", "have", "to", "fast", "for", "12", "hours"],
          extra: ["must", "can", "might"],
          a: ["All", "patients", "have", "to", "fast", "for", "12", "hours"],
          successTip: "🔬 Brilliant! **HAVE TO fast** - external medical requirement!"
        },
        {
          type: "mc",
          q: "**Insurance agent:** 'You ___ get a second opinion before we approve the surgery. It's company policy.'",
          options: ["have to", "must", "could", "should"],
          a: "have to",
          successTip: "🏥 Great! **HAVE TO** for insurance company rules!"
        },
        {
          type: "input",
          q: "**Airport health officer:** 'All travelers from that country ___ show a negative COVID test.'",
          alts: ["have to", "have got to"],
          a: "have to",
          successTip: "✈️ Perfect! **HAVE TO** for government regulations!"
        },
        {
          type: "mc",
          q: "**Medical student:** 'We ___ complete 500 hours of clinical practice to graduate. It's mandatory.'",
          options: ["have to", "must", "can", "might"],
          a: "have to",
          successTip: "🎓 Excellent! **HAVE TO** for educational requirements!"
        },
        {
          type: "mc",
          q: "**Physiotherapist:** 'My patients ___ come twice a week. That's what the treatment protocol requires.'",
          options: ["have to", "must", "should", "can"],
          a: "have to",
          successTip: "🏋️ Outstanding! **HAVE TO** for protocol requirements!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🚫 SET 3: MUST NOT - Prohibition
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u2-s3",
      title: "Set 3: Must Not - Prohibitions",
      icon: "🚫",
      context: `**Medical Center - Post-Operative Care Unit**

Estás en la sala de cuidados post-operatorios. Aquí el personal médico da advertencias sobre comportamientos PELIGROSOS o PROHIBIDOS. Cuando algo NO ESTÁ PERMITIDO (porque es peligroso), usamos "MUST NOT".

**Medical Center - Post-Operative Care Unit**

You're in the post-operative care unit. Here medical staff give warnings about DANGEROUS or FORBIDDEN behaviors. When something is NOT ALLOWED (because it's dangerous), we use "MUST NOT".`,

      questions: [
        {
          type: "mc",
          q: "**Doctor to pregnant patient:** 'You ___ drink alcohol during pregnancy. It can harm your baby.'",
          options: ["must not", "don't have to", "can't", "shouldn't"],
          a: "must not",
          successTip: "🚫 **MUST NOT** = prohibition! Something is not allowed for safety!"
        },
        {
          type: "mc",
          q: "**Nurse:** 'You ___ get your stitches wet for the next 10 days, or the wound might get infected.'",
          options: ["must not", "don't have to", "can't", "won't"],
          a: "must not",
          successTip: "💧 Perfect! **MUST NOT get wet** = strong prohibition!"
        },
        {
          type: "input",
          q: "**Pharmacist:** 'You ___ ___ take these pills on an empty stomach. Always eat first.'",
          alts: ["must not", "mustn't"],
          a: "must not",
          successTip: "💊 Great! **MUST NOT** warns of dangerous actions!"
        },
        {
          type: "mc",
          q: "**Physical therapist:** 'You ___ put any weight on that ankle for three weeks. Use your crutches!'",
          options: ["must not", "don't have to", "can't", "won't"],
          a: "must not",
          successTip: "🦴 Excellent! **MUST NOT** = strict prohibition for healing!"
        },
        {
          type: "mc",
          q: "**Dentist:** 'You ___ eat anything hard for 24 hours after the extraction. Your gum needs time to heal.'",
          options: ["must not", "don't have to", "can't", "shouldn't"],
          a: "must not",
          successTip: "🦷 Perfect! **MUST NOT eat** = prohibition after dental work!"
        },
        {
          type: "order",
          q: "**Doctor to heart patient:** Arrange the critical lifestyle restriction:",
          words: ["You", "must", "not", "smoke", "anymore"],
          extra: ["can", "should", "have"],
          a: ["You", "must", "not", "smoke", "anymore"],
          successTip: "❤️ Brilliant! **MUST NOT smoke** = absolute prohibition!"
        },
        {
          type: "mc",
          q: "**Surgeon:** 'You ___ drive for six weeks after the operation. It's too dangerous.'",
          options: ["must not", "don't have to", "can't", "won't"],
          a: "must not",
          successTip: "🚗 Great! **MUST NOT drive** = prohibition for safety!"
        },
        {
          type: "input",
          q: "**Nurse to diabetic patient:** 'You ___ ___ skip your insulin injections. It's extremely dangerous.'",
          alts: ["must not", "mustn't"],
          a: "must not",
          successTip: "💉 Perfect! **MUST NOT skip** = prohibition for survival!"
        },
        {
          type: "mc",
          q: "**Doctor:** 'You ___ stop taking your antibiotics early, even if you feel better. Finish the full course.'",
          options: ["must not", "don't have to", "can't", "shouldn't"],
          a: "must not",
          successTip: "💊 Excellent! **MUST NOT stop early** = prohibition!"
        },
        {
          type: "mc",
          q: "**Physiotherapist:** 'You ___ stretch without warming up first. You could tear a muscle.'",
          options: ["must not", "don't have to", "can't", "won't"],
          a: "must not",
          successTip: "🏃 Outstanding! **MUST NOT stretch** without prep = prohibition!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // ✅ SET 4: DON'T HAVE TO - No Obligation
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u2-s4",
      title: "Set 4: Don't Have To - Optional",
      icon: "✅",
      context: `**Medical Center - Discharge & Follow-Up**

Estás en la zona de alta médica. Los pacientes están mejorando y muchas restricciones ya no son necesarias. Cuando algo es OPCIONAL o YA NO ES NECESARIO, usamos "DON'T HAVE TO".

**Medical Center - Discharge & Follow-Up**

You're in the medical discharge area. Patients are improving and many restrictions are no longer necessary. When something is OPTIONAL or NO LONGER NECESSARY, we use "DON'T HAVE TO".`,

      questions: [
        {
          type: "mc",
          q: "**Doctor:** 'Good news! You ___ come back for another appointment. Your leg is completely healed.'",
          options: ["don't have to", "must not", "can't", "shouldn't"],
          a: "don't have to",
          successTip: "✅ **DON'T HAVE TO** = no obligation! It's not necessary!"
        },
        {
          type: "mc",
          q: "**Nurse:** 'You ___ take painkillers if you're not in pain. They're optional.'",
          options: ["don't have to", "must not", "can't", "shouldn't"],
          a: "don't have to",
          successTip: "💊 Perfect! **DON'T HAVE TO take** = optional, your choice!"
        },
        {
          type: "input",
          q: "**Physical therapist:** 'You ___ ___ ___ do all 10 exercises. Start with 5 if you're tired.'",
          alts: ["don't have to", "do not have to"],
          a: "don't have to",
          successTip: "🏋️ Great! **DON'T HAVE TO do all** = not necessary!"
        },
        {
          type: "mc",
          q: "**Dentist:** 'You ___ use mouthwash, but it can help prevent cavities.'",
          options: ["don't have to", "must not", "can't", "shouldn't"],
          a: "don't have to",
          successTip: "🦷 Excellent! **DON'T HAVE TO use** = optional but helpful!"
        },
        {
          type: "mc",
          q: "**Doctor:** 'You ___ fast before this blood test. You can eat normally.'",
          options: ["don't have to", "must not", "can't", "shouldn't"],
          a: "don't have to",
          successTip: "🔬 Perfect! **DON'T HAVE TO fast** = not necessary!"
        },
        {
          type: "order",
          q: "**Pharmacist:** Arrange the sentence about optional medication:",
          words: ["You", "don't", "have", "to", "take", "vitamins"],
          extra: ["must", "can't", "not"],
          a: ["You", "don't", "have", "to", "take", "vitamins"],
          successTip: "💊 Brilliant! **DON'T HAVE TO take** = optional!"
        },
        {
          type: "mc",
          q: "**Surgeon:** 'You ___ wear the bandage at night once the wound has closed.'",
          options: ["don't have to", "must not", "can't", "shouldn't"],
          a: "don't have to",
          successTip: "🩹 Great! **DON'T HAVE TO wear** = no longer necessary!"
        },
        {
          type: "input",
          q: "**Doctor:** 'You ___ ___ ___ stay in the hospital. You can go home today.'",
          alts: ["don't have to", "do not have to"],
          a: "don't have to",
          successTip: "🏥 Perfect! **DON'T HAVE TO stay** = you're free to go!"
        },
        {
          type: "mc",
          q: "**Physiotherapist:** 'You ___ use ice on your knee, but heat therapy works just as well.'",
          options: ["don't have to", "must not", "can't", "shouldn't"],
          a: "don't have to",
          successTip: "🧊 Excellent! **DON'T HAVE TO use ice** = optional method!"
        },
        {
          type: "mc",
          q: "**Nurse:** 'You ___ remove the stitches yourself. Come back and we'll do it for you.'",
          options: ["don't have to", "must not", "can't", "shouldn't"],
          a: "don't have to",
          successTip: "✂️ Outstanding! **DON'T HAVE TO remove** = we'll handle it!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🔄 SET 5: MUST vs HAVE TO (Comparison)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u2-s5",
      title: "Set 5: Must vs Have To",
      icon: "⚖️",
      context: `**Medical Conference Room - Training Session**

Estás en una sesión de entrenamiento médico donde se explica la diferencia crucial:

➡️ **MUST** = obligación personal/fuerte (opinión del doctor, urgencia médica)
➡️ **HAVE TO** = obligación externa (reglas del hospital, leyes, protocolos)

**Medical Conference Room - Training Session**

You're in a medical training session where the crucial difference is explained:

➡️ **MUST** = personal/strong obligation (doctor's opinion, medical urgency)
➡️ **HAVE TO** = external obligation (hospital rules, laws, protocols)`,

      questions: [
        {
          type: "match",
          q: "Match each obligation type to the correct sentence:",
          pairs: [
            { left: "MUST (strong personal)", right: "Doctor: 'You must rest, or you won't heal.'" },
            { left: "HAVE TO (external rule)", right: "Receptionist: 'You have to fill out this form.'" },
            { left: "MUST NOT (prohibition)", right: "Nurse: 'You must not get the stitches wet.'" },
            { left: "DON'T HAVE TO (optional)", right: "Doctor: 'You don't have to come back again.'" }
          ],
          successTip: "⚖️ Perfect! You understand **MUST** (strong) vs **HAVE TO** (external)!"
        },
        {
          type: "mc",
          q: "**Which is MORE personal and emotional?** 'I ___ help my sick grandmother. I feel responsible.'",
          options: ["must", "have to"],
          a: "must",
          successTip: "❤️ **MUST** = personal obligation, emotional connection!"
        },
        {
          type: "mc",
          q: "**Which is an EXTERNAL requirement?** 'Medical students ___ pass five exams to graduate.'",
          options: ["have to", "must"],
          a: "have to",
          successTip: "🎓 **HAVE TO** = external system/rule requirement!"
        },
        {
          type: "mc",
          q: "**Doctor giving strong advice:** 'You ___ lose weight. Your health is at serious risk.'",
          options: ["must", "have to"],
          a: "must",
          successTip: "⚠️ **MUST** = strong personal professional opinion!"
        },
        {
          type: "mc",
          q: "**Hospital policy:** 'All nurses ___ work one weekend shift per month.'",
          options: ["have to", "must"],
          a: "have to",
          successTip: "📋 **HAVE TO** = institutional policy/rule!"
        },
        {
          type: "input",
          q: "**Emergency:** 'We ___ operate now! There's no time!'",
          alts: ["must"],
          a: "must",
          successTip: "🚨 **MUST** = urgent personal professional judgment!"
        },
        {
          type: "mc",
          q: "**Legal requirement:** 'Pharmacies ___ check ID before selling certain medications.'",
          options: ["have to", "must"],
          a: "have to",
          successTip: "⚖️ **HAVE TO** = legal external requirement!"
        },
        {
          type: "mc",
          q: "**Parent to sick child:** 'You ___ stay in bed today. You have a fever.'",
          options: ["must", "have to"],
          a: "must",
          successTip: "🤒 **MUST** = strong personal parental authority!"
        },
        {
          type: "order",
          q: "**Arrange the MUST obligation:** A strong professional recommendation:",
          words: ["You", "must", "quit", "smoking", "immediately"],
          extra: ["have", "to", "can"],
          a: ["You", "must", "quit", "smoking", "immediately"],
          successTip: "🚭 **MUST quit** = strong personal professional advice!"
        },
        {
          type: "order",
          q: "**Arrange the HAVE TO obligation:** An external insurance requirement:",
          words: ["You", "have", "to", "get", "pre-approval"],
          extra: ["must", "can", "should"],
          a: ["You", "have", "to", "get", "pre-approval"],
          successTip: "📋 **HAVE TO get** = external company requirement!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🔄 SET 6: MUST NOT vs DON'T HAVE TO (Critical!)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u2-s6",
      title: "Set 6: Must Not vs Don't Have To",
      icon: "⚠️",
      context: `**Medical Emergency Training - Critical Distinctions**

¡ATENCIÓN! Esta es la diferencia MÁS IMPORTANTE:

🚫 **MUST NOT** = PROHIBIDO (dangerous, forbidden, not allowed)
✅ **DON'T HAVE TO** = OPCIONAL (optional, not necessary, your choice)

¡Confundir estos puede ser PELIGROSO en contextos médicos!

**Medical Emergency Training - Critical Distinctions**

ATTENTION! This is the MOST IMPORTANT difference:

🚫 **MUST NOT** = FORBIDDEN (dangerous, prohibited, not allowed)
✅ **DON'T HAVE TO** = OPTIONAL (not necessary, your choice)

Confusing these can be DANGEROUS in medical contexts!`,

      questions: [
        {
          type: "match",
          q: "Match: Prohibition vs Optional",
          pairs: [
            { left: "MUST NOT = prohibited", right: "You must not smoke here. It's forbidden." },
            { left: "DON'T HAVE TO = optional", right: "You don't have to come. It's your choice." },
            { left: "MUST NOT = dangerous", right: "You must not touch that. It's hot!" },
            { left: "DON'T HAVE TO = unnecessary", right: "You don't have to pay. It's free." }
          ],
          successTip: "⚠️ Perfect! **MUST NOT** (forbidden) ≠ **DON'T HAVE TO** (optional)!"
        },
        {
          type: "mc",
          q: "**Doctor:** 'You ___ drink alcohol with this medication. It's dangerous!' (Choose the PROHIBITION)",
          options: ["must not", "don't have to"],
          a: "must not",
          successTip: "🚫 **MUST NOT** = forbidden/prohibited for safety!"
        },
        {
          type: "mc",
          q: "**Nurse:** 'You ___ stay overnight. You can go home if you want.' (Choose the OPTIONAL)",
          options: ["don't have to", "must not"],
          a: "don't have to",
          successTip: "✅ **DON'T HAVE TO** = optional, your choice!"
        },
        {
          type: "mc",
          q: "**Surgeon:** 'You ___ lift anything heavy for 8 weeks!' (Choose the PROHIBITION)",
          options: ["must not", "don't have to"],
          a: "must not",
          successTip: "⚠️ **MUST NOT lift** = prohibited, dangerous!"
        },
        {
          type: "mc",
          q: "**Doctor:** 'You ___ take vitamins, but they might help.' (Choose the OPTIONAL)",
          options: ["don't have to", "must not"],
          a: "don't have to",
          successTip: "💊 **DON'T HAVE TO take** = optional, not necessary!"
        },
        {
          type: "input",
          q: "**WARNING:** 'You ___ ___ eat shellfish. You're allergic!'",
          alts: ["must not", "mustn't"],
          a: "must not",
          successTip: "🦐 **MUST NOT eat** = absolutely forbidden!"
        },
        {
          type: "input",
          q: "**Pharmacist:** 'You ___ ___ ___ take all the pills. 2-3 per day is fine.'",
          alts: ["don't have to", "do not have to"],
          a: "don't have to",
          successTip: "💊 **DON'T HAVE TO take all** = flexible, optional!"
        },
        {
          type: "order",
          q: "**Arrange the PROHIBITION:** About post-surgery showering:",
          words: ["You", "must", "not", "shower", "this", "week"],
          extra: ["have", "to", "don't"],
          a: ["You", "must", "not", "shower", "this", "week"],
          successTip: "🚿 **MUST NOT shower** = forbidden for healing!"
        },
        {
          type: "order",
          q: "**Arrange the OPTIONAL:** About bringing a relative:",
          words: ["You", "don't", "have", "to", "bring", "anyone"],
          extra: ["must", "not", "can"],
          a: ["You", "don't", "have", "to", "bring", "anyone"],
          successTip: "👥 **DON'T HAVE TO bring** = optional, your choice!"
        },
        {
          type: "mc",
          q: "**DANGER:** 'You ___ drive after taking this medicine. It causes drowsiness.'",
          options: ["must not", "don't have to"],
          a: "must not",
          successTip: "🚗 **MUST NOT drive** = dangerous, forbidden!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🏥 SET 7: Real Medical Scenarios (Mixed Practice)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u2-s7",
      title: "Set 7: Real Medical Scenarios",
      icon: "🩺",
      context: `**General Hospital - Mixed Departments**

Hoy trabajas en diferentes departamentos del hospital. Escucharás TODOS los tipos de obligaciones: must, have to, must not, y don't have to. Debes identificar cuál es el correcto según el contexto.

**General Hospital - Mixed Departments**

Today you work in different hospital departments. You'll hear ALL types of obligations: must, have to, must not, and don't have to. You must identify which is correct according to the context.`,

      questions: [
        {
          type: "mc",
          q: "**Dr. Lee:** 'Your blood sugar is very high. You ___ reduce your sugar intake immediately.'",
          options: ["must", "don't have to", "can", "might"],
          a: "must",
          successTip: "🩺 **MUST reduce** = critical medical necessity!"
        },
        {
          type: "mc",
          q: "**Hospital policy:** 'All surgical patients ___ remove jewelry before entering the operating room.'",
          options: ["have to", "must not", "don't have to", "can"],
          a: "have to",
          successTip: "💍 **HAVE TO remove** = mandatory hospital protocol!"
        },
        {
          type: "mc",
          q: "**Physical therapist:** 'You ___ push through pain during exercises. Stop if something hurts!'",
          options: ["must not", "don't have to", "have to", "can"],
          a: "must not",
          successTip: "⚠️ **MUST NOT push through pain** = prohibited for safety!"
        },
        {
          type: "mc",
          q: "**Doctor:** 'Good news! You ___ take antibiotics anymore. The infection is gone.'",
          options: ["don't have to", "must not", "have to", "must"],
          a: "don't have to",
          successTip: "💊 **DON'T HAVE TO take** = no longer necessary!"
        },
        {
          type: "input",
          q: "**Paramedic:** 'This patient ___ get to a hospital immediately! She's having a heart attack.'",
          alts: ["must", "has to"],
          a: "must",
          successTip: "🚑 **MUST get** = urgent emergency necessity!"
        },
        {
          type: "mc",
          q: "**Lab technician:** 'You ___ sign this consent form before we can do the blood test. It's required.'",
          options: ["have to", "must not", "don't have to", "can"],
          a: "have to",
          successTip: "📋 **HAVE TO sign** = legal requirement!"
        },
        {
          type: "mc",
          q: "**Oncologist:** 'You ___ miss any chemotherapy sessions. Every treatment is crucial.'",
          options: ["must not", "don't have to", "have to", "can"],
          a: "must not",
          successTip: "💉 **MUST NOT miss** = prohibited, life-critical!"
        },
        {
          type: "order",
          q: "**Arrange the sentence:** Doctor explaining flexible medication timing:",
          words: ["You", "don't", "have", "to", "take", "painkillers", "at", "night"],
          extra: ["must", "not", "can't"],
          a: ["You", "don't", "have", "to", "take", "painkillers", "at", "night"],
          successTip: "🌙 **DON'T HAVE TO take at night** = flexible, optional!"
        },
        {
          type: "input",
          q: "**Psychiatrist:** 'You ___ ___ stop your medication suddenly. It can cause serious side effects.'",
          alts: ["must not", "mustn't"],
          a: "must not",
          successTip: "💊 **MUST NOT stop suddenly** = dangerous prohibition!"
        },
        {
          type: "mc",
          q: "**Nutritionist:** 'You ___ eat vegetables if you want to be healthy. They're essential.'",
          options: ["must", "don't have to", "can", "might"],
          a: "must",
          successTip: "🥗 **MUST eat vegetables** = essential for health!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🎯 SET 8: Questions with Obligations
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u2-s8",
      title: "Set 8: Asking About Obligations",
      icon: "❓",
      context: `**Hospital Information Desk - Patient Questions**

Eres recepcionista en el hospital. Los pacientes hacen muchas preguntas sobre obligaciones y requisitos. Para preguntar sobre obligaciones externas, usamos:

❓ **"Do I have to...?"** (¿Tengo que...?)
❓ **"Do we have to...?"** (¿Tenemos que...?)

**Hospital Information Desk - Patient Questions**

You're a receptionist at the hospital. Patients ask many questions about obligations and requirements. To ask about external obligations, we use:

❓ **"Do I have to...?"** (Do I need to...?)
❓ **"Do we have to...?"** (Do we need to...?)`,

      questions: [
        {
          type: "order",
          q: "**Arrange the question:** Patient asking about appointment requirements:",
          words: ["Do", "I", "have", "to", "bring", "my", "medical", "records?"],
          extra: ["must", "can", "should"],
          a: ["Do", "I", "have", "to", "bring", "my", "medical", "records?"],
          successTip: "❓ Perfect question! **Do I have to...?** asks about requirements!"
        },
        {
          type: "order",
          q: "**Arrange the question:** Child asking parent about medicine:",
          words: ["Do", "I", "have", "to", "take", "this", "medicine?"],
          extra: ["must", "can", "should"],
          a: ["Do", "I", "have", "to", "take", "this", "medicine?"],
          successTip: "💊 Great! **Do I have to take...?** asks if it's necessary!"
        },
        {
          type: "mc",
          q: "**Patient to surgeon:** '___ I have to stay in the hospital overnight after the surgery?'",
          options: ["Do", "Must", "Can", "Should"],
          a: "Do",
          successTip: "❓ **Do I have to...?** = correct question form!"
        },
        {
          type: "input",
          q: "**Patient to doctor:** '___ ___ ___ ___ come back for another check-up?'",
          alts: ["Do I have to", "Do i have to"],
          a: "Do I have to",
          successTip: "❓ Perfect! **Do I have to come back?** asks about necessity!"
        },
        {
          type: "mc",
          q: "**Anxious patient:** '___ I stop eating before the blood test tomorrow?'",
          options: ["Do I have to", "Must I", "Can I", "Should I"],
          a: "Do I have to",
          successTip: "🔬 **Do I have to stop eating?** = asking about requirement!"
        },
        {
          type: "order",
          q: "**Arrange the question:** Patient asking about medication restrictions:",
          words: ["Do", "I", "have", "to", "avoid", "dairy", "products?"],
          extra: ["must", "can", "not"],
          a: ["Do", "I", "have", "to", "avoid", "dairy", "products?"],
          successTip: "🥛 Excellent! **Do I have to avoid...?** asks about restrictions!"
        },
        {
          type: "mc",
          q: "**Patient to physiotherapist:** '___ I have to do all 15 exercises every day?'",
          options: ["Do", "Must", "Can", "Should"],
          a: "Do",
          successTip: "🏋️ **Do I have to do all...?** = asking if it's required!"
        },
        {
          type: "input",
          q: "**Child to nurse:** '___ ___ ___ ___ get a shot today?'",
          alts: ["Do I have to", "Do i have to"],
          a: "Do I have to",
          successTip: "💉 Great question! **Do I have to get...?** asks about requirement!"
        },
        {
          type: "mc",
          q: "**Patient:** '___ I have to take these pills with food, or can I take them on an empty stomach?'",
          options: ["Do", "Must", "Can", "Should"],
          a: "Do",
          successTip: "💊 Perfect! **Do I have to take...?** asks about necessity!"
        },
        {
          type: "order",
          q: "**Arrange the question:** Patient asking surgeon about post-op restrictions:",
          words: ["Do", "I", "have", "to", "take", "time", "off", "work?"],
          extra: ["must", "can", "should"],
          a: ["Do", "I", "have", "to", "take", "time", "off", "work?"],
          successTip: "💼 Brilliant! **Do I have to take time off?** = requirement question!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🌍 SET 9: Obligations Beyond Healthcare (Daily Life)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u2-s9",
      title: "Set 9: Obligations in Daily Life",
      icon: "🏠",
      context: `**Beyond the Hospital - Real World Obligations**

Las obligaciones no solo existen en el hospital. En la vida diaria encontramos must, have to, must not, y don't have to en aeropuertos, gimnasios, hoteles, y más.

**Beyond the Hospital - Real World Obligations**

Obligations don't only exist in hospitals. In daily life we find must, have to, must not, and don't have to at airports, gyms, hotels, and more.`,

      questions: [
        {
          type: "mc",
          q: "**Airport security:** 'All passengers ___ remove their shoes and belts before going through security.'",
          options: ["have to", "must not", "don't have to", "can"],
          a: "have to",
          successTip: "✈️ **HAVE TO remove** = airport security requirement!"
        },
        {
          type: "mc",
          q: "**Parent to teenager:** 'You ___ finish your homework before you can play video games.'",
          options: ["must", "don't have to", "can", "might"],
          a: "must",
          successTip: "📚 **MUST finish** = strong parental rule!"
        },
        {
          type: "mc",
          q: "**Hotel policy:** 'Guests ___ check out by 11am or pay for an extra night.'",
          options: ["have to", "must not", "don't have to", "can"],
          a: "have to",
          successTip: "🏨 **HAVE TO check out** = hotel policy!"
        },
        {
          type: "mc",
          q: "**Gym rules:** 'Members ___ use their phones in the quiet zone. It disturbs others.'",
          options: ["must not", "don't have to", "have to", "can"],
          a: "must not",
          successTip: "📱 **MUST NOT use phones** = prohibited in quiet zone!"
        },
        {
          type: "input",
          q: "**Restaurant host:** 'Good news! You ___ ___ ___ make a reservation. We have tables available.'",
          alts: ["don't have to", "do not have to"],
          a: "don't have to",
          successTip: "🍽️ **DON'T HAVE TO make reservation** = not necessary!"
        },
        {
          type: "mc",
          q: "**Environmental law:** 'Companies ___ dispose of chemicals properly. It's illegal to dump them.'",
          options: ["must", "don't have to", "can", "might"],
          a: "must",
          successTip: "♻️ **MUST dispose properly** = legal requirement!"
        },
        {
          type: "mc",
          q: "**Library rules:** 'You ___ return books late. There's a $2 fee per day.'",
          options: ["must not", "don't have to", "have to", "can"],
          a: "must not",
          successTip: "📚 **MUST NOT return late** = rule with penalty!"
        },
        {
          type: "order",
          q: "**Arrange the sentence:** Parent explaining household chores:",
          words: ["You", "don't", "have", "to", "clean", "your", "room", "today"],
          extra: ["must", "not", "can't"],
          a: ["You", "don't", "have", "to", "clean", "your", "room", "today"],
          successTip: "🧹 **DON'T HAVE TO clean today** = optional, flexible!"
        },
        {
          type: "input",
          q: "**Swimming pool rules:** 'Children under 12 ___ be accompanied by an adult.'",
          alts: ["must"],
          a: "must",
          successTip: "🏊 **MUST be accompanied** = safety rule!"
        },
        {
          type: "mc",
          q: "**Boss to employee:** 'You ___ work this weekend if you don't want to. It's voluntary overtime.'",
          options: ["don't have to", "must not", "have to", "must"],
          a: "don't have to",
          successTip: "💼 **DON'T HAVE TO work** = optional, voluntary!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🎓 SET 10: Advanced Mixed Practice (A2→B1)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u2-s10",
      title: "Set 10: Advanced Mixed Practice",
      icon: "🏆",
      context: `**Final Mastery Challenge - Complex Medical Scenarios**

Éste es el desafío final. Enfrentarás oraciones COMPLEJAS con MÚLTIPLES obligaciones en el mismo contexto. Necesitas identificar correctamente cada tipo: must, have to, must not, y don't have to.

**Final Mastery Challenge - Complex Medical Scenarios**

This is the final challenge. You'll face COMPLEX sentences with MULTIPLE obligations in the same context. You need to correctly identify each type: must, have to, must not, and don't have to.`,

      questions: [
        {
          type: "mc",
          q: "**Complete the medical advice:** 'You ___ take this antibiotic three times daily, but you ___ take it with food—water is fine.'",
          options: [
            "must / don't have to",
            "have to / must not",
            "must not / have to",
            "don't have to / must"
          ],
          a: "must / don't have to",
          successTip: "💊 Perfect! **MUST take** (essential) / **DON'T HAVE TO take with food** (optional)!"
        },
        {
          type: "mc",
          q: "**Doctor's discharge instructions:** 'You ___ come back in two weeks, but you ___ do any heavy lifting until then.'",
          options: [
            "have to / must not",
            "must not / have to",
            "don't have to / must",
            "must / don't have to"
          ],
          a: "have to / must not",
          successTip: "🏥 Excellent! **HAVE TO come back** (required) / **MUST NOT lift** (prohibited)!"
        },
        {
          type: "order",
          q: "**Arrange the complex sentence:** Physical therapist explaining recovery:",
          words: ["You", "must", "rest", "this", "week,", "but", "you", "don't", "have", "to", "stay", "in", "bed"],
          extra: ["can", "not", "should"],
          a: ["You", "must", "rest", "this", "week,", "but", "you", "don't", "have", "to", "stay", "in", "bed"],
          successTip: "🛏️ Brilliant! **MUST rest** (essential) but **DON'T HAVE TO stay in bed** (flexible)!"
        },
        {
          type: "mc",
          q: "**Hospital policy vs doctor's advice:** 'You ___ pay the bill before leaving (policy), and I strongly recommend you ___ smoke anymore (advice).'",
          options: [
            "have to / must not",
            "must / don't have to",
            "don't have to / must",
            "must not / have to"
          ],
          a: "have to / must not",
          successTip: "💰 Perfect! **HAVE TO pay** (external rule) / **MUST NOT smoke** (strong advice)!"
        },
        {
          type: "input",
          q: "**Complex medical advice:** 'You ___ ___ ___ take painkillers if you're comfortable, but you ___ ___ exceed the recommended dose.'",
          alts: ["don't have to", "must not"],
          a: "don't have to",
          successTip: "💊 Great! **DON'T HAVE TO take** (optional) but **MUST NOT exceed** (dangerous)!"
        },
        {
          type: "match",
          q: "Match each situation to the correct obligation type:",
          pairs: [
            { left: "Hospital says: check in at 7am", right: "HAVE TO (external rule)" },
            { left: "Doctor says: quit smoking now", right: "MUST (strong professional advice)" },
            { left: "Doctor says: don't swim for 2 weeks", right: "MUST NOT (prohibition)" },
            { left: "Nurse says: you can go home today", right: "DON'T HAVE TO stay (optional)" }
          ],
          successTip: "🎯 Outstanding! You've mastered the four obligation types!"
        },
        {
          type: "mc",
          q: "**Legal vs medical obligation:** 'You ___ show your insurance card (law), and you ___ follow my treatment plan (medical necessity).'",
          options: [
            "have to / must",
            "must / have to",
            "must not / don't have to",
            "don't have to / must not"
          ],
          a: "have to / must",
          successTip: "📋 Excellent! **HAVE TO show** (legal) / **MUST follow** (medical)!"
        },
        {
          type: "order",
          q: "**Arrange the complex discharge instruction:**",
          words: ["You", "don't", "have", "to", "change", "the", "bandage", "daily,", "but", "you", "must", "keep", "it", "dry"],
          extra: ["not", "can", "should"],
          a: ["You", "don't", "have", "to", "change", "the", "bandage", "daily,", "but", "you", "must", "keep", "it", "dry"],
          successTip: "🩹 Brilliant! **DON'T HAVE TO change daily** (optional) but **MUST keep dry** (essential)!"
        },
        {
          type: "input",
          q: "**Emergency room triage:** 'This patient ___ see a doctor immediately, but these patients ___ ___ ___ wait—they're not critical.'",
          alts: ["must", "have to", "don't have to", "do not have to"],
          a: "must",
          successTip: "🚑 Perfect! **MUST see immediately** (critical) vs **don't have to** (can wait)!"
        },
        {
          type: "mc",
          q: "**FINAL MASTERY:** 'You ___ take vitamins (optional), you ___ exercise regularly (strong advice), you ___ have surgery (required by doctor), and you ___ eat sugar (forbidden).'",
          options: [
            "don't have to / must / have to / must not",
            "must / have to / must not / don't have to",
            "have to / must not / don't have to / must",
            "must not / don't have to / must / have to"
          ],
          a: "don't have to / must / have to / must not",
                    successTip: "🏆 AMAZING! You've mastered all four: optional, strong advice, requirement, and prohibition! **UNIT 2 COMPLETE!**"
        }
      ]
    }
  ],

  // ═══════════════════════════════════════════════════════════════
  // 📖 READING SET (m9-u2-reading)
  // ═══════════════════════════════════════════════════════════════
  // ═══════════════════════════════════════════════════════════════
  // 📖 READING SET (m9-u2-reading)
  // ═══════════════════════════════════════════════════════════════

  readingSet: {
    id: "m9-u2-reading",
    title: "Reading Practice: Medical Instructions",
    category: "reading",
    
    passages: [
      // ─────────────────────────────────────────────────────────
      // 📄 PASSAGE 1: Emergency Room Instructions
      // ─────────────────────────────────────────────────────────
      {
        context: `**Emergency Room - Dr. Martinez treating broken arm**

El Dr. Martinez está dando instrucciones a un paciente con brazo fracturado.

**Emergency Room - Dr. Martinez treating broken arm**

Dr. Martinez is giving instructions to a patient with a broken arm.`,

        text: `You **must** keep your arm in the cast for six weeks. You **must not** get the cast wet when showering. However, you **don't have to** take painkillers if you're not in pain—they're optional.`,

        questions: [
          {
            type: "mc",
            q: "How long must the patient keep the cast on?",
            options: ["Six weeks", "Two weeks", "Ten days", "Three months"],
            a: "Six weeks",
            successTip: "⏰ Correct! The patient **must** keep it for six weeks!"
          },
          {
            type: "mc",
            q: "What **must not** the patient do?",
            options: ["Get the cast wet", "Take painkillers", "See the doctor", "Rest"],
            a: "Get the cast wet",
            successTip: "💧 Right! **Must not get wet** = prohibition!"
          },
          {
            type: "mc",
            q: "Are painkillers mandatory?",
            options: ["No, they're optional", "Yes, they're required", "Yes, they're prohibited", "No information given"],
            a: "No, they're optional",
            successTip: "💊 Perfect! **Don't have to take** = optional!"
          }
        ]
      },

      // ─────────────────────────────────────────────────────────
      // 📄 PASSAGE 2: Hospital Policy Notice
      // ─────────────────────────────────────────────────────────
      {
        context: `**Hospital Reception - Registration Requirements**

Letrero en la recepción del hospital sobre políticas obligatorias.

**Hospital Reception - Registration Requirements**

Sign at hospital reception about mandatory policies.`,

        text: `All patients **have to** show photo ID and insurance cards at registration. Visitors **must** wear masks in patient rooms. Children under 12 **must not** visit the ICU for safety reasons.`,

        questions: [
          {
            type: "mc",
            q: "What do all patients **have to** show?",
            options: ["Photo ID and insurance cards", "Medical records", "Appointment letters", "Nothing"],
            a: "Photo ID and insurance cards",
            successTip: "📋 Correct! **Have to show** = hospital requirement!"
          },
          {
            type: "mc",
            q: "Who **must not** visit the ICU?",
            options: ["Children under 12", "All visitors", "Doctors", "Nurses"],
            a: "Children under 12",
            successTip: "🚫 Right! **Must not visit** = prohibited for safety!"
          }
        ]
      },

      // ─────────────────────────────────────────────────────────
      // 📄 PASSAGE 3: Pharmacist Instructions
      // ─────────────────────────────────────────────────────────
      {
        context: `**Pharmacy Counter - Medication Instructions**

La farmacéutica explica cómo tomar un nuevo medicamento.

**Pharmacy Counter - Medication Instructions**

The pharmacist explains how to take a new medication.`,

        text: `You **must** take these antibiotics twice daily for ten days. You **must not** stop early, even if you feel better. You **don't have to** take them with a large meal—a snack is enough.`,

        questions: [
          {
            type: "mc",
            q: "How often must the patient take the antibiotics?",
            options: ["Twice daily", "Once daily", "Three times daily", "Every other day"],
            a: "Twice daily",
            successTip: "💊 Correct! **Must take** twice daily = medical instruction!"
          },
          {
            type: "mc",
            q: "Can the patient stop taking the medicine when feeling better?",
            options: ["No, they must not stop early", "Yes, it's optional", "Yes, if pain is gone", "No information"],
            a: "No, they must not stop early",
            successTip: "⚠️ Perfect! **Must not stop early** = prohibition!"
          },
          {
            type: "mc",
            q: "Is a large meal required with the medication?",
            options: ["No, a snack is enough", "Yes, it's mandatory", "Yes, or it won't work", "No food allowed"],
            a: "No, a snack is enough",
            successTip: "🍞 Great! **Don't have to** eat large meal = not necessary!"
          }
        ]
      },

      // ─────────────────────────────────────────────────────────
      // 📄 PASSAGE 4: Post-Surgery Discharge
      // ─────────────────────────────────────────────────────────
      {
        context: `**Discharge Instructions - Going Home After Surgery**

Enfermera dando instrucciones de alta después de cirugía.

**Discharge Instructions - Going Home After Surgery**

Nurse giving discharge instructions after surgery.`,

        text: `You **must** rest for two weeks and **must not** lift anything heavy. You **have to** take your medication as prescribed. Good news: you **don't have to** come back to the hospital. Your local doctor can remove the stitches.`,

        questions: [
          {
            type: "mc",
            q: "What **must** the patient do for two weeks?",
            options: ["Rest", "Exercise", "Work", "Travel"],
            a: "Rest",
            successTip: "🛏️ Correct! **Must rest** = medical necessity!"
          },
          {
            type: "mc",
            q: "Does the patient **have to** return to the hospital?",
            options: ["No, the local doctor can help", "Yes, it's required", "Yes, for stitches", "No information"],
            a: "No, the local doctor can help",
            successTip: "✅ Perfect! **Don't have to** return = not necessary!"
          }
        ]
      },

      // ─────────────────────────────────────────────────────────
      // 📄 PASSAGE 5: Physical Therapy Session
      // ─────────────────────────────────────────────────────────
      {
        context: `**Physical Therapy Clinic - Exercise Instructions**

Fisioterapeuta explicando rutina de ejercicios de recuperación.

**Physical Therapy Clinic - Exercise Instructions**

Physical therapist explaining recovery exercise routine.`,

        text: `You **must** do these exercises every day to recover fully. You **must not** push through pain—stop immediately if something hurts. You **don't have to** do all ten exercises at first. Start with five and gradually increase.`,

        questions: [
          {
            type: "mc",
            q: "How often must the patient do the exercises?",
            options: ["Every day", "Twice a week", "Once a week", "Only when in pain"],
            a: "Every day",
            successTip: "🏋️ Correct! **Must do every day** = essential for recovery!"
          },
          {
            type: "mc",
            q: "What **must not** the patient do?",
            options: ["Push through pain", "Exercise daily", "Start with five exercises", "See the therapist"],
            a: "Push through pain",
            successTip: "⚠️ Right! **Must not push through pain** = dangerous prohibition!"
          },
          {
            type: "mc",
            q: "Must the patient do all ten exercises immediately?",
            options: ["No, they can start with five", "Yes, all ten are required", "Yes, or it won't work", "No exercises needed"],
            a: "No, they can start with five",
            successTip: "✅ Great! **Don't have to** do all = flexible, gradual!"
          }
        ]
      },

      // ─────────────────────────────────────────────────────────
      // 📄 PASSAGE 6: Pregnancy Consultation
      // ─────────────────────────────────────────────────────────
      {
        context: `**Maternity Clinic - Prenatal Care Instructions**

Doctora dando consejos a paciente embarazada.

**Maternity Clinic - Prenatal Care Instructions**

Doctor giving advice to pregnant patient.`,

        text: `You **must** take prenatal vitamins daily. You **must not** drink alcohol or smoke—it's dangerous for your baby. You **don't have to** eat for two, but you should eat healthy, balanced meals.`,

        questions: [
          {
            type: "mc",
            q: "What **must** the pregnant patient take daily?",
            options: ["Prenatal vitamins", "Painkillers", "Antibiotics", "Sleeping pills"],
            a: "Prenatal vitamins",
            successTip: "💊 Correct! **Must take** vitamins = essential for pregnancy!"
          },
          {
            type: "mc",
            q: "What is **prohibited** during pregnancy?",
            options: ["Alcohol and smoking", "Exercise", "Vitamins", "Healthy food"],
            a: "Alcohol and smoking",
            successTip: "🚫 Perfect! **Must not** drink/smoke = dangerous prohibition!"
          },
          {
            type: "mc",
            q: "Does the patient **have to** eat double portions?",
            options: ["No, just healthy balanced meals", "Yes, eat for two people", "Yes, it's required", "No information"],
            a: "No, just healthy balanced meals",
            successTip: "🥗 Great! **Don't have to** eat for two = common myth debunked!"
          }
        ]
      },

      // ─────────────────────────────────────────────────────────
      // 📄 PASSAGE 7: Blood Test Requirements
      // ─────────────────────────────────────────────────────────
      {
        context: `**Laboratory - Pre-Test Instructions**

Técnico de laboratorio explicando requisitos para examen de sangre.

**Laboratory - Pre-Test Instructions**

Lab technician explaining blood test requirements.`,

        text: `You **have to** fast for 12 hours before this blood test. You **must not** eat or drink anything except water during that time. You **don't have to** stop taking your regular medications unless your doctor tells you to.`,

        questions: [
          {
            type: "mc",
            q: "What **requirement** must patients follow before the test?",
            options: ["Fast for 12 hours", "Exercise heavily", "Stop all medications", "Drink lots of juice"],
            a: "Fast for 12 hours",
            successTip: "🔬 Correct! **Have to fast** = external test requirement!"
          },
          {
            type: "mc",
            q: "What **must not** patients consume during fasting?",
            options: ["Anything except water", "Water", "Medications", "Nothing is restricted"],
            a: "Anything except water",
            successTip: "🚫 Right! **Must not eat/drink** = prohibition (except water)!"
          }
        ]
      }
    ]
  },


  // ═══════════════════════════════════════════════════════════════
  // 🏆 REVIEW SET (m9-u2-review)
  // ═══════════════════════════════════════════════════════════════

  reviewSet: {
    id: "m9-u2-review",
    title: "Unit Review: Obligations Mastery",
    category: "review",
    context: `**🏆 UNIT 2 FINAL REVIEW - Obligations Mastery Test**

Este es el repaso final de la Unidad 2. Demuestra que dominas las 4 formas de obligación:

✅ **MUST** = obligación fuerte/personal
✅ **HAVE TO** = obligación externa/reglas
✅ **MUST NOT** = prohibición
✅ **DON'T HAVE TO** = opcional/no necesario

**🏆 UNIT 2 FINAL REVIEW - Obligations Mastery Test**

This is the final review of Unit 2. Prove that you master the 4 forms of obligation:

✅ **MUST** = strong/personal obligation
✅ **HAVE TO** = external obligation/rules
✅ **MUST NOT** = prohibition
✅ **DON'T HAVE TO** = optional/not necessary`,

    questions: [
      {
        type: "mc",
        q: "**Doctor to patient:** 'Your cholesterol is dangerously high. You ___ change your diet immediately.'",
        options: ["must", "have to", "don't have to", "must not"],
        a: "must",
        successTip: "💊 **MUST** = strong professional medical advice!"
      },
      {
        type: "mc",
        q: "**Hospital receptionist:** 'All new patients ___ complete this registration form before seeing the doctor.'",
        options: ["have to", "must", "don't have to", "must not"],
        a: "have to",
        successTip: "📋 **HAVE TO** = external administrative requirement!"
      },
      {
        type: "mc",
        q: "**Surgeon:** 'You ___ swim for four weeks after the operation. The chlorine could cause infection.'",
        options: ["must not", "don't have to", "have to", "must"],
        a: "must not",
        successTip: "🚫 **MUST NOT swim** = prohibited for medical safety!"
      },
      {
        type: "mc",
        q: "**Doctor:** 'Your arm is fully healed now. You ___ wear the sling anymore.'",
        options: ["don't have to", "must not", "have to", "must"],
        a: "don't have to",
        successTip: "✅ **DON'T HAVE TO wear** = no longer necessary!"
      },
      {
        type: "match",
        q: "**REVIEW: Match the obligation types to examples**",
        pairs: [
          { left: "MUST (strong personal)", right: "Doctor: 'You must quit smoking now.'" },
          { left: "HAVE TO (external rule)", right: "Hospital: 'You have to pay the bill.'" },
          { left: "MUST NOT (prohibition)", right: "Nurse: 'You must not drive after surgery.'" },
          { left: "DON'T HAVE TO (optional)", right: "Doctor: 'You don't have to come back.'" }
        ],
        successTip: "🎯 Perfect! You understand all four obligation types!"
      },
      {
        type: "order",
        q: "**REVIEW: Arrange the question about medical requirements**",
        words: ["Do", "I", "have", "to", "take", "painkillers", "every", "day?"],
        extra: ["must", "can", "not"],
        a: ["Do", "I", "have", "to", "take", "painkillers", "every", "day?"],
        successTip: "❓ Excellent! **Do I have to...?** = asking about requirement!"
      },
      {
        type: "mc",
        q: "**Complex scenario:** 'You ___ take vitamins (optional), but you ___ take your blood pressure medication (essential).'",
        options: [
          "don't have to / must",
          "must / don't have to",
          "must not / have to",
          "have to / must not"
        ],
        a: "don't have to / must",
        successTip: "💊 Perfect! Optional vs essential = **don't have to / must**!"
      },
      {
        type: "mc",
        q: "**Which is MORE personal and emotional?** 'I ___ help my sick mother. I'm her only child.'",
        options: ["must", "have to"],
        a: "must",
        successTip: "❤️ **MUST** = personal emotional obligation, family duty!"
      },
      {
        type: "input",
        q: "**REVIEW: Critical prohibition:** 'You ___ ___ mix alcohol with this medication. It's extremely dangerous.'",
        alts: ["must not", "mustn't"],
        a: "must not",
        successTip: "⚠️ **MUST NOT mix** = absolutely forbidden, dangerous!"
      },
      {
        type: "mc",
        q: "**Which expresses an EXTERNAL rule?** 'Pilots ___ pass a medical exam every year to keep their license.'",
        options: ["have to", "must"],
        a: "have to",
        successTip: "✈️ **HAVE TO pass** = external aviation authority regulation!"
      },
      {
        type: "order",
        q: "**REVIEW: Arrange the complex prohibition sentence**",
        words: ["You", "must", "not", "eat", "anything", "for", "12", "hours", "before", "surgery"],
        extra: ["have", "to", "don't"],
        a: ["You", "must", "not", "eat", "anything", "for", "12", "hours", "before", "surgery"],
        successTip: "🚫 Brilliant! **MUST NOT eat** before surgery = critical prohibition!"
      },
      {
        type: "mc",
        q: "**FINAL MASTERY QUESTION:** 'You ___ attend the seminar (optional), you ___ submit the report (required by boss), you ___ follow the doctor's advice (strong personal), and you ___ share confidential data (forbidden).'",
        options: [
          "don't have to / have to / must / must not",
          "must / have to / must not / don't have to",
          "have to / must not / don't have to / must",
          "must not / don't have to / have to / must"
        ],
        a: "don't have to / have to / must / must not",
        successTip: "🎉 **INCREDIBLE!** You've mastered all obligations: optional, external rule, strong personal, and prohibition! **CONGRATULATIONS - UNIT 2 OBLIGATIONS COMPLETE!** 🏆✨"
      }
    ]
  }
};
